/**
 * cdn-domain-optimization-tab.tsx — Optimization tab for a CDN domain (act-as).
 *
 * Three cards: Compression (algorithms + min size + content-type filter),
 * Image Optimization (enabled + rules field-array), Timeouts (6 number inputs).
 * Each card has its own GET/PUT endpoint. Mutations gated through ActAsMutationConfirm.
 *
 * Endpoints:
 *   GET/PUT /api/v3/cdn/domains/:id/compression
 *   GET/PUT /api/v3/cdn/domains/:id/image-optimization
 *   GET/PUT /api/v3/cdn/domains/:id/timeouts
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import { Section, SettingRow, ActionBar } from '@vnetwork/ui/layout';
import {
  Card, CardHeader, CardTitle, CardDescription, CardBody,
  Button, IconButton, Input, Switch, Skeleton, Badge,
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert } from '@vnetwork/ui/feedback';
import { TabError } from '@/pages/customers/detail/tab-states';
import {
  useActAsCDNCompression,
  useActAsCDNImageOptimization,
  useActAsCDNTimeouts,
  actAsCDNSecurityKeys,
  type CompressionConfig,
  type ImageOptimizationConfig,
  type ImageOptimizationRule,
  type TimeoutsConfig,
} from '@/api/act-as-cdn-security';
import { actAsClient } from '@/api/act-as-client';
import { ActAsMutationConfirm } from '../act-as-mutation-confirm';
import { NumericInput } from '@/components/numeric-input';

/* ── Compression form ────────────────────────────────────────────────────── */

const ALGORITHMS = ['gzip', 'br', 'zstd'] as const;

interface CompForm {
  enabled: boolean;
  algorithms: string[];
  minimum_size_bytes: string;
  content_type_filter: string;
}

function hydrateComp(c?: CompressionConfig | null): CompForm {
  return {
    enabled: c?.enabled ?? false,
    algorithms: c?.algorithms ?? ['gzip', 'br'],
    minimum_size_bytes: String(c?.minimum_size_bytes ?? 1024),
    content_type_filter: (c?.content_type_filter ?? []).join(', '),
  };
}

function compPayload(f: CompForm): CompressionConfig {
  return {
    enabled: f.enabled,
    algorithms: f.algorithms,
    minimum_size_bytes: Number(f.minimum_size_bytes) || 0,
    content_type_filter: f.content_type_filter.split(',').map((s) => s.trim()).filter(Boolean),
  };
}

/* ── Image optimisation form ─────────────────────────────────────────────── */

const IMG_FORMATS = ['original', 'webp', 'avif', 'jpeg', 'png'] as const;
const MATCH_TYPES = ['prefix', 'exact', 'suffix', 'contains', 'wildcard', 'regex'] as const;

function blankImgRule(): ImageOptimizationRule {
  return {
    name: '', priority: 10, match_type: 'prefix', match_path: '/',
    output_format: 'webp', quality: 85,
  };
}

interface ImgForm {
  enabled: boolean;
  rules: ImageOptimizationRule[];
}

function hydrateImg(c?: ImageOptimizationConfig | null): ImgForm {
  return { enabled: c?.enabled ?? false, rules: c?.rules ?? [] };
}

/* ── Timeouts form ───────────────────────────────────────────────────────── */

interface ToutForm {
  client_read_secs: string;
  client_write_secs: string;
  server_connect_secs: string;
  server_read_secs: string;
  keepalive_secs: string;
  websocket_idle_secs: string;
}

const TOUT_DEFAULTS: ToutForm = {
  client_read_secs: '60',
  client_write_secs: '60',
  server_connect_secs: '10',
  server_read_secs: '60',
  keepalive_secs: '75',
  websocket_idle_secs: '3600',
};

function hydrateTout(c?: TimeoutsConfig | null): ToutForm {
  return {
    client_read_secs:    String(c?.client_read_secs    ?? 60),
    client_write_secs:   String(c?.client_write_secs   ?? 60),
    server_connect_secs: String(c?.server_connect_secs ?? 10),
    server_read_secs:    String(c?.server_read_secs    ?? 60),
    keepalive_secs:      String(c?.keepalive_secs      ?? 75),
    websocket_idle_secs: String(c?.websocket_idle_secs ?? 3600),
  };
}

function toutPayload(f: ToutForm): TimeoutsConfig {
  return {
    client_read_secs:    Number(f.client_read_secs)    || 0,
    client_write_secs:   Number(f.client_write_secs)   || 0,
    server_connect_secs: Number(f.server_connect_secs) || 0,
    server_read_secs:    Number(f.server_read_secs)    || 0,
    keepalive_secs:      Number(f.keepalive_secs)      || 0,
    websocket_idle_secs: Number(f.websocket_idle_secs) || 0,
  };
}

/* ── Component ───────────────────────────────────────────────────────────── */

interface Props { domainId: string }

export function CdnDomainOptimizationTab({ domainId }: Props) {
  const { t } = useTranslation();

  const compQ = useActAsCDNCompression(domainId);
  const imgQ  = useActAsCDNImageOptimization(domainId);
  const toutQ = useActAsCDNTimeouts(domainId);

  const [comp, setComp]         = useState<CompForm>(hydrateComp());
  const [compSnap, setCompSnap] = useState<CompForm>(hydrateComp());
  const [img, setImg]           = useState<ImgForm>(hydrateImg());
  const [imgSnap, setImgSnap]   = useState<ImgForm>(hydrateImg());
  const [tout, setTout]         = useState<ToutForm>(TOUT_DEFAULTS);
  const [toutSnap, setToutSnap] = useState<ToutForm>(TOUT_DEFAULTS);

  const [compConfirm, setCompConfirm] = useState(false);
  const [imgConfirm,  setImgConfirm]  = useState(false);
  const [toutConfirm, setToutConfirm] = useState(false);

  useEffect(() => { const n = hydrateComp(compQ.data); setComp(n); setCompSnap(n); }, [compQ.data]);
  useEffect(() => { const n = hydrateImg(imgQ.data);   setImg(n);  setImgSnap(n);  }, [imgQ.data]);
  useEffect(() => { const n = hydrateTout(toutQ.data); setTout(n); setToutSnap(n); }, [toutQ.data]);

  const compDirty = useMemo(() => JSON.stringify(compPayload(comp))  !== JSON.stringify(compPayload(compSnap)), [comp, compSnap]);
  const imgDirty  = useMemo(() => JSON.stringify(img)  !== JSON.stringify(imgSnap), [img, imgSnap]);
  const toutDirty = useMemo(() => JSON.stringify(toutPayload(tout)) !== JSON.stringify(toutPayload(toutSnap)), [tout, toutSnap]);

  const isLoading = compQ.isLoading || imgQ.isLoading || toutQ.isLoading;
  const isError   = compQ.isError   || imgQ.isError   || toutQ.isError;

  if (isLoading) return <Skeleton style={{ height: 400 }} />;
  if (isError) {
    return (
      <TabError
        title={t('cdn_domain.optimization_tab.load_error', 'Failed to load optimization settings')}
        onRetry={() => { void compQ.refetch(); void imgQ.refetch(); void toutQ.refetch(); }}
      />
    );
  }

  /* helpers */
  const toggleAlgo = (algo: string) =>
    setComp((c) => ({
      ...c,
      algorithms: c.algorithms.includes(algo)
        ? c.algorithms.filter((a) => a !== algo)
        : [...c.algorithms, algo],
    }));

  const setImgRule = (i: number, k: keyof ImageOptimizationRule, v: unknown) =>
    setImg((f) => ({ ...f, rules: f.rules.map((r, idx) => idx === i ? { ...r, [k]: v } : r) }));
  const addImgRule   = () => setImg((f) => ({ ...f, rules: [...f.rules, blankImgRule()] }));
  const deleteImgRule = (i: number) => setImg((f) => ({ ...f, rules: f.rules.filter((_, idx) => idx !== i) }));

  const setToutField = (k: keyof ToutForm, v: string) => setTout((f) => ({ ...f, [k]: v }));

  return (
    <Section
      title={t('cdn_domain.optimization_tab.title', 'Optimization')}
      description={t('cdn_domain.optimization_tab.description', 'Compression, image optimization, and connection timeout settings.')}
    >
      <Alert tone="info" style={{ marginBottom: 'var(--vn-space-4)' }}>
        {t('cdn_domain.optimization_tab.beta_notice', 'Beta — each card saves independently.')}
      </Alert>

      <div style={{ display: 'grid', gap: 'var(--vn-space-6)' }}>

        {/* ── Compression ── */}
        <Card>
          <CardHeader>
            <CardTitle>{t('cdn_domain.optimization_tab.section_compression', 'Compression')}</CardTitle>
            <CardDescription>{t('cdn_domain.optimization_tab.section_compression_desc', 'Compress responses at the edge.')}</CardDescription>
          </CardHeader>
          <CardBody style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            <SettingRow
              title={t('cdn_domain.optimization_tab.field_comp_enabled', 'Compression enabled')}
              control={<Switch checked={comp.enabled} onCheckedChange={(v) => setComp((c) => ({ ...c, enabled: v }))} />}
            />
            {comp.enabled && (
              <>
                <div>
                  <div style={{ fontWeight: 500, fontSize: 'var(--vn-font-size-sm)', marginBottom: 'var(--vn-space-2)' }}>
                    {t('cdn_domain.optimization_tab.field_algorithms', 'Algorithms')}
                  </div>
                  <div style={{ display: 'flex', gap: 'var(--vn-space-2)' }}>
                    {ALGORITHMS.map((a) => (
                      <Badge
                        key={a}
                        tone={comp.algorithms.includes(a) ? 'info' : 'neutral'}
                        appearance={comp.algorithms.includes(a) ? 'solid' : 'subtle'}
                        style={{ cursor: 'pointer' }}
                        onClick={() => toggleAlgo(a)}
                      >
                        {a}
                      </Badge>
                    ))}
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--vn-space-3)' }}>
                  <Field label={t('cdn_domain.optimization_tab.field_min_size', 'Minimum size (bytes)')}>
                    {(p) => (
                      <NumericInput {...p} value={comp.minimum_size_bytes}
                        onChange={(e) => setComp((c) => ({ ...c, minimum_size_bytes: e.target.value }))} />
                    )}
                  </Field>
                  <Field
                    label={t('cdn_domain.optimization_tab.field_content_types', 'Content-type filter')}
                    description={t('cdn_domain.optimization_tab.field_content_types_desc', 'Comma-separated MIME types. Empty = all.')}
                  >
                    {(p) => (
                      <Input {...p} value={comp.content_type_filter} placeholder="text/html, application/json"
                        onChange={(e) => setComp((c) => ({ ...c, content_type_filter: e.target.value }))} />
                    )}
                  </Field>
                </div>
              </>
            )}
          </CardBody>
          <ActionBar
            end={
              <>
                <Button variant="secondary" disabled={!compDirty} onClick={() => setComp(compSnap)}>{t('common.cancel', 'Cancel')}</Button>
                <Button disabled={!compDirty} onClick={() => setCompConfirm(true)}>{t('cdn_domain.optimization_tab.save_compression', 'Save compression')}</Button>
              </>
            }
          />
        </Card>

        {/* ── Image optimisation ── */}
        <Card>
          <CardHeader
            actions={
              <Button variant="secondary" size="sm" startIcon={<Plus size={14} />} onClick={addImgRule}>
                {t('cdn_domain.optimization_tab.add_img_rule', 'Add rule')}
              </Button>
            }
          >
            <CardTitle>{t('cdn_domain.optimization_tab.section_image_opt', 'Image Optimization')}</CardTitle>
            <CardDescription>{t('cdn_domain.optimization_tab.section_image_opt_desc', 'Convert and resize images at the edge per path rule.')}</CardDescription>
          </CardHeader>
          <CardBody style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            <SettingRow
              title={t('cdn_domain.optimization_tab.field_img_enabled', 'Image optimization enabled')}
              control={<Switch checked={img.enabled} onCheckedChange={(v) => setImg((f) => ({ ...f, enabled: v }))} />}
            />
            {img.enabled && img.rules.map((rule, i) => (
              <div key={i} style={{
                display: 'grid', gap: 'var(--vn-space-3)',
                padding: 'var(--vn-space-3)', borderRadius: 'var(--vn-radius-md)',
                background: 'var(--vn-bg-subtle)',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: 500, fontSize: 'var(--vn-font-size-sm)' }}>
                    {t('cdn_domain.optimization_tab.img_rule_label', 'Rule {{n}}', { n: i + 1 })}
                  </span>
                  <IconButton variant="ghost" size="sm" aria-label="Delete" onClick={() => deleteImgRule(i)}>
                    <Trash2 size={14} />
                  </IconButton>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 'var(--vn-space-3)' }}>
                  <Field label={t('cdn_domain.optimization_tab.field_img_name', 'Name')}>
                    {(p) => <Input {...p} value={rule.name} placeholder="webp-all" onChange={(e) => setImgRule(i, 'name', e.target.value)} />}
                  </Field>
                  <Field label={t('cdn_domain.optimization_tab.field_img_priority', 'Priority')}>
                    {(p) => <NumericInput {...p} value={rule.priority} onChange={(e) => setImgRule(i, 'priority', Number(e.target.value))} />}
                  </Field>
                  <Field label={t('cdn_domain.optimization_tab.field_img_match_type', 'Match type')}>
                    {(p) => (
                      <Select value={rule.match_type} onValueChange={(v) => setImgRule(i, 'match_type', v)}>
                        <SelectTrigger {...p}><SelectValue /></SelectTrigger>
                        <SelectContent>{MATCH_TYPES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                      </Select>
                    )}
                  </Field>
                  <Field label={t('cdn_domain.optimization_tab.field_img_match_path', 'Match path')}>
                    {(p) => <Input {...p} value={rule.match_path} placeholder="/images/*" onChange={(e) => setImgRule(i, 'match_path', e.target.value)} />}
                  </Field>
                  <Field label={t('cdn_domain.optimization_tab.field_img_format', 'Output format')}>
                    {(p) => (
                      <Select value={rule.output_format} onValueChange={(v) => setImgRule(i, 'output_format', v)}>
                        <SelectTrigger {...p}><SelectValue /></SelectTrigger>
                        <SelectContent>{IMG_FORMATS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                      </Select>
                    )}
                  </Field>
                  <Field label={t('cdn_domain.optimization_tab.field_img_quality', 'Quality (0–100)')}>
                    {(p) => <NumericInput {...p} min={0} max={100} value={rule.quality} onChange={(e) => setImgRule(i, 'quality', Number(e.target.value))} />}
                  </Field>
                  <Field label={t('cdn_domain.optimization_tab.field_img_max_w', 'Max width (px)')}>
                    {(p) => <NumericInput {...p} value={rule.max_width ?? ''} placeholder="—" onChange={(e) => setImgRule(i, 'max_width', e.target.value ? Number(e.target.value) : undefined)} />}
                  </Field>
                  <Field label={t('cdn_domain.optimization_tab.field_img_max_h', 'Max height (px)')}>
                    {(p) => <NumericInput {...p} value={rule.max_height ?? ''} placeholder="—" onChange={(e) => setImgRule(i, 'max_height', e.target.value ? Number(e.target.value) : undefined)} />}
                  </Field>
                </div>
              </div>
            ))}
          </CardBody>
          <ActionBar
            end={
              <>
                <Button variant="secondary" disabled={!imgDirty} onClick={() => setImg(imgSnap)}>{t('common.cancel', 'Cancel')}</Button>
                <Button disabled={!imgDirty} onClick={() => setImgConfirm(true)}>{t('cdn_domain.optimization_tab.save_image_opt', 'Save image optimization')}</Button>
              </>
            }
          />
        </Card>

        {/* ── Timeouts ── */}
        <Card>
          <CardHeader>
            <CardTitle>{t('cdn_domain.optimization_tab.section_timeouts', 'Timeouts')}</CardTitle>
            <CardDescription>{t('cdn_domain.optimization_tab.section_timeouts_desc', 'Connection and I/O timeout values in seconds.')}</CardDescription>
          </CardHeader>
          <CardBody>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 'var(--vn-space-4)' }}>
              {(
                [
                  ['client_read_secs',    t('cdn_domain.optimization_tab.tout_client_read',    'Client read')],
                  ['client_write_secs',   t('cdn_domain.optimization_tab.tout_client_write',   'Client write')],
                  ['server_connect_secs', t('cdn_domain.optimization_tab.tout_server_connect', 'Server connect')],
                  ['server_read_secs',    t('cdn_domain.optimization_tab.tout_server_read',    'Server read')],
                  ['keepalive_secs',      t('cdn_domain.optimization_tab.tout_keepalive',      'Keep-alive')],
                  ['websocket_idle_secs', t('cdn_domain.optimization_tab.tout_ws_idle',        'WebSocket idle')],
                ] as [keyof ToutForm, string][]
              ).map(([k, label]) => (
                <Field key={k} label={label}>
                  {(p) => (
                    <NumericInput {...p} value={tout[k]}
                      onChange={(e) => setToutField(k, e.target.value)} />
                  )}
                </Field>
              ))}
            </div>
          </CardBody>
          <ActionBar
            end={
              <>
                <Button variant="secondary" disabled={!toutDirty} onClick={() => setTout(toutSnap)}>{t('common.cancel', 'Cancel')}</Button>
                <Button disabled={!toutDirty} onClick={() => setToutConfirm(true)}>{t('cdn_domain.optimization_tab.save_timeouts', 'Save timeouts')}</Button>
              </>
            }
          />
        </Card>
      </div>

      <ActAsMutationConfirm
        open={compConfirm} onOpenChange={setCompConfirm}
        title={t('cdn_domain.optimization_tab.confirm_comp_title', 'Save compression settings')}
        summary={t('cdn_domain.optimization_tab.comp_summary', 'Update compression algorithms, minimum size, and content-type filter.')}
        queryKeysToInvalidate={[actAsCDNSecurityKeys.compression(domainId)]}
        mutationFn={() => actAsClient.put(`/cdn/domains/${domainId}/compression`, compPayload(comp))}
        onSuccess={() => setCompSnap(comp)}
      />
      <ActAsMutationConfirm
        open={imgConfirm} onOpenChange={setImgConfirm}
        title={t('cdn_domain.optimization_tab.confirm_img_title', 'Save image optimization')}
        summary={t('cdn_domain.optimization_tab.img_summary', 'Update {{n}} image optimization rule(s).', { n: img.rules.length })}
        queryKeysToInvalidate={[actAsCDNSecurityKeys.imageOptimization(domainId)]}
        mutationFn={() => actAsClient.put(`/cdn/domains/${domainId}/image-optimization`, { enabled: img.enabled, rules: img.rules })}
        onSuccess={() => setImgSnap(img)}
      />
      <ActAsMutationConfirm
        open={toutConfirm} onOpenChange={setToutConfirm}
        title={t('cdn_domain.optimization_tab.confirm_tout_title', 'Save timeouts')}
        summary={t('cdn_domain.optimization_tab.tout_summary', 'Update connection and I/O timeout values for this domain.')}
        queryKeysToInvalidate={[actAsCDNSecurityKeys.timeouts(domainId)]}
        mutationFn={() => actAsClient.put(`/cdn/domains/${domainId}/timeouts`, toutPayload(tout))}
        onSuccess={() => setToutSnap(tout)}
      />
    </Section>
  );
}
